/**
 * sidebarConfig.js — Single Source of Truth para navegação do GlobalSidebar
 * 
 * Este arquivo centraliza TODOS os itens de navegação (ativos + planejados)
 * e fornece helpers para filtrar por role, sport, feature flags e status.
 * 
 * Baseado na matriz oficial: /docs/navigation/GLOBAL_SIDEBAR_MATRIX.md
 * 
 * @version 3.0.0 — Performance OS B2B (PROMPT 2: SIDEBAR_ITEMS v3.0)
 * @date 2026-02-20
 * 
 * SEMÂNTICA DE ROLES (RBAC):
 * - roles: [] → PÚBLICO (visível para TODOS os roles)
 * - roles: ['ATLETA'] → RESTRITO (visível apenas para atletas)
 * - capability: string → requer capability OU ADMIN override
 * - route: null + status: 'planned' → item desabilitado ("Em breve")
 */

import { FEATURES } from '../config/features.js';
import { isNavItemOutOfScope } from '../config/demoScope.js';

// ════════════════════════════════════════════════════════════════════════════
// GROUP ORDER — Ordem de exibição dos grupos (Performance OS v3.0)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Define a ordem EXATA de renderização dos grupos na sidebar.
 * NÃO usar Object.keys() — usar esta lista explícita.
 */
export const GROUP_ORDER = [
  'atleta',        // 1. Minha Área — Atleta
  'treinador',     // 2. Minha Área — Treinador
  'prep_fisico',   // 3. Minha Área — Preparador Físico
  'nutricionista', // 3b. Minha Área — Nutricionista
  'biomecanico',   // 3c. Minha Área — Biomecânico
  'gestor',        // 4. Minha Área — Gestor
  'gestor_admin',  // 4b. Administração — Gestor (Financeiro, Suporte, Config, Permissões)
  'intelligence',  // 5. Inteligência (compartilhado, inclui Biblioteca)
  'governance',    // 7. Administração (Super Admin only)
  // institutional — renderizado no footer do GlobalSidebar (não duplicar aqui)
];

// ════════════════════════════════════════════════════════════════════════════
// TYPES & ENUMS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Status de implementação de um item
 * @typedef {'active' | 'planned'} NavItemStatus
 * - active: rota implementada, navegação habilitada
 * - planned: planejado, exibido como disabled com badge "Em breve"
 */

/**
 * Roles disponíveis no sistema (RBAC)
 * @typedef {'ATLETA' | 'TREINADOR' | 'PREPARADOR_FISICO' | 'GESTOR' | 'ADMIN'} Role
 */

/**
 * Esportes disponíveis
 * @typedef {'football' | 'football_american' | 'swimming' | 'triathlon' | 'multi'} SportKey
 * - multi: independente de esporte (aparece em todos)
 */

/**
 * Estrutura de um item de navegação
 * @typedef {Object} NavItem
 * @property {string} id - Identificador único
 * @property {string} label - Texto exibido
 * @property {string|null} route - Caminho da rota (null = planejado sem rota)
 * @property {NavItemStatus} status - Estado de implementação
 * @property {SportKey[]} sports - Esportes onde o item aparece
 * @property {Role[]} roles - Roles permitidas ([] = todos)
 * @property {string} icon - Nome do ícone (Lucide React)
 * @property {string} group - Seção/agrupamento
 * @property {number} order - Ordem de exibição dentro do grupo
 * @property {string|null} [badge] - Badge opcional (NEW, BETA, AI, FOUNDER, Em breve)
 * @property {string} [notes] - Descrição/observações
 * @property {string} [match] - Tipo de match de rota ('exact' | 'startsWith')
 * @property {boolean} [collapsible] - Se a seção é colapsável
 * @property {string} [capability] - Capability requerida (ex: arena.recruiter.read)
 */

// ════════════════════════════════════════════════════════════════════════════
// SIDEBAR ITEMS — DEFINIÇÃO COMPLETA v3.0
// ════════════════════════════════════════════════════════════════════════════

/**
 * @type {NavItem[]}
 */
export const SIDEBAR_ITEMS = [
  // ══════════════════════════════════════════════════════════════════════════
  // 1. ATLETA (Minha Área — tudo do atleta agrupado)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'nav-athlete-day',
    label: 'Hoje',
    route: '/athlete/today',
    status: 'active',
    sports: ['multi'],
    roles: ['ATLETA'],
    icon: 'calendar-check',
    group: 'atleta',
    order: 1,
    match: 'exact',
    notes: 'Página-do-dia (home pós-login): treino de hoje + dieta do dia + widgets'
  },
  {
    id: 'nav-athlete-home',
    label: 'VUKZ Assist',
    route: '/assist',
    status: 'active',
    sports: ['multi'],
    roles: ['ATLETA'],
    icon: 'compass',
    group: 'atleta',
    order: 13,
    match: 'exact',
    notes: 'VUKZ Assist — desceu pro fim do grupo (07/07/2026): a home do atleta é a página-do-dia'
  },
  {
    id: 'nav-athlete-today',
    label: 'Treino',
    route: '/athlete/training',
    status: 'active',
    sports: ['multi'],
    roles: ['ATLETA'],
    icon: 'dumbbell',
    group: 'atleta',
    order: 2,
    match: 'startsWith',
    notes: 'Timeline unificada — UM link de treino (pedido 04/07): técnico e físico em seções dentro da página; o antigo link Treino Físico saiu (rota /treino-fisico de pé pra staff)',
    featureFlag: 'ENABLE_TRAINING'
  },
  // Vukz Swim Assist removido — funcionalidade embutida nos agentes AI
  {
    id: 'nav-athlete-my-performance',
    label: 'Minha Performance',
    route: '/my/performance',
    status: 'active',
    sports: ['multi'],
    roles: ['ATLETA'],
    icon: 'trophy',
    group: 'atleta',
    order: 6,
    match: 'exact',
    notes: 'Rankings e resultados do atleta — VUKZ design'
  },
  {
    id: 'nav-athlete-wellness',
    label: 'Meu Bem-Estar',
    route: '/my/wellness',
    status: 'active',
    sports: ['multi'],
    roles: ['ATLETA'],
    icon: 'heart',
    group: 'atleta',
    order: 7,
    match: 'startsWith',
    notes: 'Wellness tracking pessoal'
  },
  {
    // T5 Onda 2: item desabilitado cosméticamente.
    // Decisão de produto: "biomecânica sai do atleta no lançamento;
    // religa por plano/add-on depois." Onda 3 aplica enforcement real.
    // Pré-requisito para religar: resolver consentimento LGPD de menores
    // em biomech_observations (ver migration 288 nota LGPD).
    id: 'nav-athlete-biomechanics',
    label: 'Perfil Biomecânico',
    route: null,                // removido cosméticamente — Onda 3 é o bloqueio real
    status: 'planned',          // exibe como disabled ("Em breve") na sidebar
    sports: ['swimming'],
    roles: ['ATLETA'],
    icon: 'scan-line',
    group: 'atleta',
    order: 8,
    notes: 'Desabilitado no lançamento. Religa via add-on biomecânica (Onda 3).'
  },
  {
    id: 'nav-athlete-injuries',
    label: 'Minhas Lesões',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: ['ATLETA'],
    icon: 'bandage',
    group: 'atleta',
    order: 9,
    notes: 'Histórico pessoal de lesões'
  },
  {
    id: 'nav-athlete-rehab',
    label: 'Minha Reabilitação',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: ['ATLETA'],
    icon: 'heart-pulse',
    group: 'atleta',
    order: 9,
    notes: 'Protocolos de reabilitação'
  },
  {
    id: 'nav-athlete-nutrition',
    label: 'Minha Dieta',
    route: '/my/diet',
    status: 'active',
    sports: ['multi'],
    roles: ['ATLETA'],
    icon: 'utensils',
    group: 'atleta',
    order: 10,
    match: 'exact',
    notes: 'Detalhe completo da dieta do atleta autenticado (self-service: backend resolve via g.user_id) — gate final demo jul/2026'
  },
  {
    id: 'nav-athlete-profile',
    label: 'Meu Perfil',
    route: '/atleta/perfil',
    status: 'active',
    sports: ['multi'],
    roles: ['ATLETA'],
    icon: 'user',
    group: 'atleta',
    order: 11,
    match: 'exact',
    notes: 'Resultados, competições e medidas corporais do atleta'
  },
  {
    id: 'nav-athlete-messages',
    label: 'Mensagens',
    route: '/chat',
    status: 'active',
    sports: ['multi'],
    roles: ['ATLETA'],
    icon: 'mail',
    group: 'atleta',
    order: 12,
    match: 'startsWith',
    notes: 'Chat 1:1 com staff — treinador, preparador, gestor'
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 2. TREINADOR (Minha Área — tudo do treinador agrupado)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'nav-coach-day',
    label: 'Hoje',
    route: '/staff/today',
    status: 'active',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'calendar-check',
    group: 'treinador',
    order: 0,
    match: 'exact',
    notes: 'Roster-do-dia: os meus atletas completando em tempo real (home pós-login)'
  },
  {
    id: 'nav-coach-home',
    label: 'VUKZ Assist',
    route: '/assist',
    status: 'active',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'compass',
    group: 'treinador',
    order: 1,
    match: 'exact',
    notes: 'VUKZ Assist — Central do Treinador (Performance OS)'
  },
  {
    id: 'nav-coach-dashboard',
    label: 'Meus Atletas',
    route: '/coach',
    status: 'active',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'users',
    group: 'treinador',
    order: 2,
    match: 'exact',
    notes: 'Dashboard principal do coach'
  },
  {
    id: 'nav-coach-groups',
    label: 'Grupos de treino',
    route: '/coach/groups',
    status: 'active',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'users',
    group: 'treinador',
    order: 3,
    match: 'startsWith',
    notes: 'Gate 3 — gestão de grupos de treino (squads)'
  },
  {
    id: 'nav-coach-training',
    label: 'Treinos',
    route: '/treinos',
    status: 'active',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'dumbbell',
    group: 'treinador',
    order: 4,
    match: 'startsWith',
    notes: 'Gestão unificada de treinos (consolidação ago/2026, era /coach/training)'
  },
  {
    id: 'nav-coach-treino-fisico',
    label: 'Treino Físico',
    route: '/treino-fisico',
    status: 'active',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'activity',
    group: 'treinador',
    order: 5,
    match: 'exact',
    notes: 'Visualizar treinos físicos e acompanhar execuções dos atletas'
  },
  {
    id: 'nav-coach-athlete-timeline',
    label: 'Treino dos atletas',
    route: '/staff/training',
    status: 'active',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'clipboard',
    group: 'treinador',
    order: 6,
    match: 'exact',
    notes: 'C3: timeline de execução dos atletas do grupo — técnico editável, físico read-only (viewer do backend)'
  },

  {
    id: 'nav-coach-my-performance',
    label: 'Performance Atletas',
    route: '/coach/performance',
    status: 'active',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'trophy',
    group: 'treinador',
    order: 6,
    match: 'exact',
    notes: 'Rankings e performance dos atletas do treinador — VUKZ design'
  },
  {
    id: 'nav-coach-periodization',
    label: 'Periodização',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'calendar-range',
    group: 'treinador',
    order: 7,
    notes: 'Macrociclo, mesociclo, microciclo'
  },
  {
    id: 'nav-coach-session-builder',
    label: 'Construtor de Sessão',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'plus-square',
    group: 'treinador',
    order: 8,
    notes: 'Editor visual de sessões (VUKZ Assist)'
  },
  {
    id: 'nav-coach-health-overview',
    label: 'Wellness Elenco',
    route: '/staff/wellness',
    status: 'active',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'heart-pulse',
    group: 'treinador',
    order: 9,
    match: 'exact',
    notes: 'Monitoramento de bem-estar do elenco — check-ins, alertas e relatórios'
  },
  {
    id: 'nav-coach-nutrition',
    label: 'Dietas dos atletas',
    route: '/coach/athlete-diets',
    status: 'active',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'utensils',
    group: 'treinador',
    order: 10,
    match: 'exact',
    notes: 'Consulta read-only dos planos alimentares dos atletas do escopo (gate final demo jul/2026) — nutricionista continua dono do plano'
  },
  {
    id: 'nav-coach-injuries',
    label: 'Controle de Lesões',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'bandage',
    group: 'treinador',
    order: 11,
    notes: 'Registro e acompanhamento de lesões do elenco'
  },
  {
    id: 'nav-coach-clearance',
    label: 'Liberações Médicas',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'check-circle',
    group: 'treinador',
    order: 12,
    notes: 'Aprovações de retorno ao treino/jogo'
  },
  {
    id: 'nav-coach-biomech-dashboard',
    label: 'Análise Biomecânica',
    route: '/biomech/performance-dashboard',
    status: 'planned',   // Not in v1 scope
    sports: ['swimming'],
    roles: ['TREINADOR'],
    icon: 'scan-line',
    group: 'treinador',
    order: 14,
    match: 'startsWith',
    notes: 'Dashboard de performance biomecânica — Fase 2'
  },
  {
    id: 'nav-coach-messages',
    label: 'Mensagens',
    route: '/chat',
    status: 'active',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'mail',
    group: 'treinador',
    order: 15,
    match: 'startsWith',
    notes: 'Chat com gestor, preparador e atletas vinculados'
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 3. PREPARADOR FÍSICO (Minha Área — hub do prep)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'nav-prep-day',
    label: 'Hoje',
    route: '/staff/today',
    status: 'active',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'calendar-check',
    group: 'prep_fisico',
    order: -1,
    match: 'exact',
    notes: 'Roster-do-dia: meus atletas completando em tempo real (home pós-login)'
  },
  {
    id: 'nav-prep-home',
    label: 'VUKZ Assist',
    route: '/assist',
    status: 'active',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'compass',
    group: 'prep_fisico',
    order: 0,
    match: 'exact',
    notes: 'VUKZ Assist — Central do Preparador Físico (Performance OS)'
  },
  {
    id: 'nav-prep-team',
    label: 'Meu Elenco',
    route: '/treinos?tab=sessions',
    status: 'active',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'users',
    group: 'prep_fisico',
    order: 1,
    match: 'startsWith',
    notes: 'Gestão unificada de treinos, tab sessões (consolidação ago/2026, era /prep/training)'
  },
  {
    id: 'nav-prep-groups',
    label: 'Grupos de treino',
    route: '/prep/groups',
    status: 'active',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'users',
    group: 'prep_fisico',
    order: 2,
    match: 'startsWith',
    notes: 'Gate 4: grupos físicos do preparador (CRUD) + natação read-only'
  },
  {
    id: 'nav-prep-plans',
    label: 'Criar Treino',
    route: '/treinos/novo?type=physical',
    status: 'active',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'clipboard',
    group: 'prep_fisico',
    order: 3,
    match: 'startsWith',
    notes: 'Criação de treinos físicos com agente IA integrado'
  },
  {
    id: 'nav-prep-athlete-timeline',
    label: 'Treino dos atletas',
    route: '/staff/training',
    status: 'active',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'dumbbell',
    group: 'prep_fisico',
    order: 3,
    match: 'exact',
    notes: 'C3: timeline de execução dos atletas do grupo — físico editável, técnico read-only (viewer do backend)'
  },
  {
    id: 'nav-prep-wellness-squad',
    label: 'Wellness Elenco',
    route: '/staff/wellness',
    status: 'active',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'heart-pulse',
    group: 'prep_fisico',
    order: 3,
    match: 'exact',
    notes: 'Monitoramento de bem-estar do elenco — check-ins, alertas e relatórios'
  },
  {
    id: 'nav-prep-sessions',
    label: 'Sessões',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'list',
    group: 'prep_fisico',
    order: 4,
    notes: 'Sessões de preparação física'
  },
  {
    id: 'nav-prep-athletes',
    label: 'Atletas',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'users',
    group: 'prep_fisico',
    order: 5,
    notes: 'Gestão individual de atletas'
  },
  {
    id: 'nav-prep-dashboard-fisico',
    label: 'Dashboard Físico',
    route: '/prep/dashboard',
    status: 'planned',   // duplicate of Monitoramento de Carga — removed from v1 nav
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'activity',
    group: 'prep_fisico',
    order: 5,
    match: 'exact',
    notes: 'Duplicado de Monitoramento de Carga — removido do nav v1'
  },
  {
    id: 'nav-prep-load',
    label: 'Monitoramento de Carga',
    route: '/prep/load',
    status: 'active',
    featureFlag: 'ENABLE_PREP_LOAD_UI', // 07/08/2026 fail-closed: tela exibia ACWR seedado (demo Acqua) como real — volta no ciclo execução→carga
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'gauge',
    group: 'prep_fisico',
    order: 6,
    match: 'startsWith',
    notes: 'Registrar carga (sRPE), ver ACWR e tendências — atrás de ENABLE_PREP_LOAD_UI'
  },
  {
    id: 'nav-prep-rehab',
    label: 'Reabilitação',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'heart-pulse',
    group: 'prep_fisico',
    order: 7,
    notes: 'Protocolos de reabilitação'
  },
  {
    id: 'nav-prep-screening',
    label: 'Avaliações Físicas',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'clipboard-check',
    group: 'prep_fisico',
    order: 8,
    notes: 'Screenings periódicos'
  },
  {
    id: 'nav-prep-nutrition',
    label: 'Dietas dos atletas',
    route: '/prep/athlete-diets',
    status: 'active',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'utensils',
    group: 'prep_fisico',
    order: 9,
    match: 'exact',
    notes: 'Consulta read-only dos planos alimentares dos atletas do escopo (gate final demo jul/2026)'
  },
  {
    id: 'nav-prep-periodization',
    label: 'Periodização',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'calendar-range',
    group: 'prep_fisico',
    order: 10,
    notes: 'Templates de periodização (linear, ondulatória, blocos)'
  },
  {
    id: 'nav-prep-automation',
    label: 'Automações',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'zap',
    group: 'prep_fisico',
    order: 11,
    notes: 'Regras IFTTT — automação de workflows (alertas, ajuste de carga, nutrição)'
  },
  {
    id: 'nav-prep-messages',
    label: 'Mensagens',
    route: '/chat',
    status: 'active',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'mail',
    group: 'prep_fisico',
    order: 12,
    match: 'startsWith',
    notes: 'Chat com gestor, treinador e atletas vinculados'
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 4. GESTOR (Performance Management — Head of Performance)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'nav-manager-day',
    label: 'Hoje',
    route: '/staff/today',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'calendar-check',
    group: 'gestor',
    order: -1,
    match: 'exact',
    notes: 'Roster-do-dia do clube inteiro, agrupado por categoria (home pós-login; leitura)'
  },
  {
    id: 'nav-manager-home',
    label: 'VUKZ Assist',
    route: '/assist',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'compass',
    group: 'gestor',
    order: 0,
    match: 'exact',
    notes: 'VUKZ Assist — Central do Gestor (Performance OS)'
  },
  {
    id: 'nav-manager-dashboard',
    label: 'Performance Overview',
    route: '/manager',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'bar-chart-3',
    group: 'gestor',
    order: 1,
    match: 'exact',
    notes: 'Visão consolidada por categoria — readiness, carga, aderência, riscos'
  },
  {
    id: 'nav-manager-calendar',
    label: 'Calendário',
    route: '/manager/calendar',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'calendar-days',
    group: 'gestor',
    order: 2,
    match: 'startsWith',
    notes: 'G2: Calendário Unificado — fonte única /gestor/calendar/events (técnico, físico, nutrição, competição + faixa de fase). Gestão de temporadas/fases em /manager/calendar/manage. Agenda deixou de existir para o Gestor.'
  },
  {
    id: 'nav-manager-athlete-timeline',
    label: 'Treino dos atletas',
    route: '/manager/training-overview',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'clipboard-list',
    group: 'gestor',
    order: 2,
    match: 'exact',
    notes: 'C3c: lentes agregadas (categoria / grupo de staff) com aderência real e ACWR; drill-down por atleta → /staff/training?athlete= (timeline read-only via viewer)'
  },
  {
    id: 'nav-manager-categories',
    label: 'Categorias',
    route: '/manager/categories',
    status: 'retired',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'users',
    group: 'gestor',
    order: 3,
    match: 'startsWith',
    notes: 'E1 (ago/2026): absorvida pela Equipe (/manager/staff?tab=categorias). Rotas /manager/categories* preservadas para deep links.'
  },
  {
    id: 'nav-manager-methodology',
    label: 'Metodologia',
    route: '/manager/methodology',
    status: 'planned',          // SEC-A lote desliga 11/07/2026 - backend atras de ENABLE_METHODOLOGY_API
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'shield',
    group: 'gestor',
    order: 4,
    match: 'startsWith',
    notes: 'Governança metodológica — princípios, alinhamento, versões'
  },
  {
    id: 'nav-manager-interventions',
    label: 'Intervenções',
    route: '/manager/interventions',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'message-square',
    group: 'gestor',
    order: 5,
    match: 'startsWith',
    notes: 'Coordenação entre áreas — decisões, ações, threads'
  },
  {
    id: 'nav-manager-strategic-plan',
    label: 'Plano Estratégico',
    route: '/manager/plan',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR', 'TREINADOR'],
    icon: 'target',
    group: 'gestor',
    order: 6,
    match: 'startsWith',
    notes: 'Hierarquia por temporada: plano geral + 2 picos (LCM/SCM) + internacionais. Gestor vê tudo; Treinador vê fatia dos seus atletas.'
  },
  {
    id: 'nav-manager-staff-permissions',
    label: 'Escopo do Staff',
    route: '/manager/staff-permissions',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'shield-check',
    group: 'gestor_admin',
    order: 3,
    match: 'startsWith',
    notes: 'Controle de acesso a ferramentas e categorias por usuário'
  },
  {
    id: 'nav-manager-setup',
    label: 'Configuração do Clube',
    route: '/manager/setup',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'settings',
    group: 'gestor_admin',
    order: 4,
    match: 'exact',
    notes: 'Wizard de primeiro acesso — 8 etapas de onboarding (inclui staff e elenco)'
  },
  {
    id: 'nav-manager-claim',
    label: 'Dados Oficiais',
    route: '/manager/claim',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'trophy',
    group: 'gestor_admin',
    order: 5,
    match: 'startsWith',
    notes: 'Vincular clube oficial para carregar atletas e resultados do banco esportivo'
  },
  {
    id: 'nav-manager-staff',
    label: 'Equipe',
    route: '/manager/staff',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'users',
    group: 'gestor',
    order: 3,
    match: 'startsWith',
    notes: 'E1 (ago/2026): Equipe = estrutura esportiva do clube — tabs Profissionais (S4/S5) e Categorias (?tab=categorias). Conceitos permanecem separados no banco; unificação é só de navegação. RosterPage preservada em /manager/roster.'
  },
  {
    id: 'nav-manager-athlete-diets',
    label: 'Dietas dos atletas',
    route: '/manager/athlete-diets',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'utensils',
    group: 'gestor',
    order: 7,
    match: 'exact',
    notes: 'Cobertura nutricional por categoria → atletas → dieta (read-only) — gate final demo jul/2026'
  },
  {
    id: 'nav-manager-messages',
    label: 'Mensagens',
    route: '/chat',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'mail',
    group: 'gestor',
    order: 8,
    match: 'startsWith',
    notes: 'Chat com todo staff e atletas do clube'
  },
  {
    id: 'nav-manager-financial',
    label: 'Financeiro',
    route: '/manager/financial',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'dollarSign',
    group: 'gestor_admin',
    order: 1,
    match: 'exact',
    notes: 'Assinatura, faturas e consumo'
  },
  {
    id: 'nav-manager-support',
    label: 'Suporte',
    route: '/manager/support',
    status: 'active',
    sports: ['multi'],
    roles: ['GESTOR'],
    icon: 'lifebuoy',
    group: 'gestor_admin',
    order: 2,
    match: 'exact',
    notes: 'Central de chamados e atendimento'
  },

  // coaching_core — REMOVIDO v3.1: itens migrados para grupo 'intelligence'

  // ══════════════════════════════════════════════════════════════════════════
  // 6. AGENDA + CRONOGRAMA (dentro de Minha Área de cada perfil)
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: 'nav-agenda-atleta',
    label: 'Agenda',
    route: '/agenda',
    status: 'active',
    sports: ['multi'],
    roles: ['ATLETA'],
    icon: 'calendar',
    group: 'atleta',
    order: 90,
    match: 'startsWith',
    notes: 'Agenda unificada — calendário, readiness, elenco, carga e operações por role'
  },

  {
    id: 'nav-agenda-treinador',
    label: 'Agenda',
    route: '/agenda',
    status: 'active',
    sports: ['multi'],
    roles: ['TREINADOR'],
    icon: 'calendar',
    group: 'treinador',
    order: 90,
    match: 'startsWith',
    notes: 'Agenda unificada'
  },

  {
    id: 'nav-agenda-prep',
    label: 'Agenda',
    route: '/agenda',
    status: 'active',
    sports: ['multi'],
    roles: ['PREPARADOR_FISICO'],
    icon: 'calendar',
    group: 'prep_fisico',
    order: 90,
    match: 'startsWith',
    notes: 'Agenda unificada'
  },

  // nav-agenda-gestor REMOVIDO (G2, 18/08/2026): Agenda deixou de existir como
  // conceito de navegação para o GESTOR — /agenda redireciona a /manager/calendar.


  // ──────────────────────────────────────────────────────────────────────────
  // 7. INTELLIGENCE (Inteligência — AI + Dados + Rankings + Vídeo)
  // ──────────────────────────────────────────────────────────────────────────

  {
    id: 'nav-analytics',
    label: 'Analytics',
    route: '/analytics',
    status: 'planned',
    sports: ['multi'],
    roles: ['ATLETA', 'TREINADOR', 'PREPARADOR_FISICO', 'GESTOR'],
    icon: 'trending-up',
    group: 'intelligence',
    order: 1,
    match: 'startsWith',
    notes: 'Performance analytics'
  },

  // ── Rankings (movidos de grupo separado) ──
  {
    id: 'nav-stats-swimming',
    label: 'Rankings Natação',
    route: '/natacao/rankings',
    status: 'active',
    sports: ['swimming'],
    roles: ['ATLETA', 'TREINADOR', 'GESTOR'],
    icon: 'trophy',
    group: 'intelligence',
    order: 3,
    match: 'exact',
    notes: 'Rankings natação — VUKZ design'
  },
  {
    id: 'nav-competition-results',
    label: 'Competições',
    route: '/competicoes',
    status: 'active',
    sports: ['swimming'],
    roles: ['ATLETA', 'TREINADOR', 'GESTOR'],
    icon: 'competitions',
    group: 'intelligence',
    order: 4,
    match: 'exact',
    notes: 'Resultados de competições de natação — VUKZ design'
  },
  // Hub Futebol — removido (v3.0)
  // Rankings Unificados — removido (Fase 2)

  // ── Swim Vision (Computer Vision + Digital Twin) ── [NOT IN V1 SCOPE]
  {
    id: 'nav-swim-vision',
    label: 'Swim Vision',
    route: '/swim-vision',
    status: 'planned',
    superAdminActive: true,   // HOTFIX 14/07/2026: SUPER_ADMIN acessa (interno); demais roles seguem 'Em breve'
    capability: 'vukz.swimvision.access', // HOTFIX 14/07/2026 (Minas): treinadores allowlisted via BIOMECHANICS_ALLOWED_EMAILS
    sports: ['swimming'],
    roles: ['TREINADOR', 'GESTOR'],
    icon: 'goggles',
    group: 'intelligence',
    order: 6,
    match: 'exact',
    notes: 'Computer Vision + Digital Twin (Minas partnership) — Fase 2'
  },
  {
    id: 'nav-swim-vision-dashboard',
    label: 'Vision Dashboard',
    route: '/swim-vision/dashboard',
    status: 'planned',
    superAdminActive: true,   // HOTFIX 14/07/2026: SUPER_ADMIN acessa (interno); demais roles seguem 'Em breve'
    capability: 'vukz.swimvision.dashboard', // HOTFIX 14/07/2026 (Minas): treinadores allowlisted via BIOMECHANICS_ALLOWED_EMAILS
    sports: ['swimming'],
    roles: ['TREINADOR', 'GESTOR'],
    icon: 'dashboard',
    group: 'intelligence',
    order: 7,
    match: 'exact',
    notes: 'Dashboard de sessões, KPIs, vinculação de provas — Fase 2'
  },

  // ── TrackVision (Athletics Computer Vision) ──
  {
    id: 'nav-track-vision',
    label: 'TrackVision',
    route: '/track-vision',
    status: 'active',
    sports: ['athletics'],
    roles: ['TREINADOR', 'GESTOR', 'ATLETA'],
    icon: 'video',
    group: 'intelligence',
    order: 8,
    match: 'exact',
    notes: 'Análise biomecânica de corridas via Azure ML (reaktcpu)'
  },
  {
    id: 'nav-track-vision-dashboard',
    label: 'Sessões TrackVision',
    route: '/track-vision/sessions',
    status: 'active',
    sports: ['athletics'],
    roles: ['TREINADOR', 'GESTOR'],
    icon: 'dashboard',
    group: 'intelligence',
    order: 9,
    match: 'exact',
    notes: 'Lista de sessões de análise biomecânica'
  },
  {
    id: 'nav-tactics-designer',
    label: 'Designer Tático',
    route: '/tactics/designer',
    status: 'active',
    sports: ['football', 'football_american'],
    roles: ['TREINADOR', 'GESTOR'],
    icon: 'layout',
    group: 'intelligence',
    order: 7,
    match: 'startsWith',
    notes: 'Quadro tático'
  },
  {
    id: 'nav-playbook-designer',
    label: 'Playbook Designer',
    route: '/playbook/designer',
    status: 'planned',          // SEC-A lote desliga 11/07/2026 - backend atras de ENABLE_PLAYBOOK_API
    sports: ['football_american'],
    roles: ['TREINADOR'],
    icon: 'book-open',
    group: 'intelligence',
    order: 8,
    match: 'startsWith',
    notes: 'Jogadas de futebol americano'
  },

  // ── Biblioteca & Pesquisa ── [NOT IN V1 SCOPE]
  {
    id: 'nav-library',
    label: 'Biblioteca Arena',
    route: '/library',
    status: 'planned',
    sports: ['multi'],
    roles: ['TREINADOR', 'PREPARADOR_FISICO', 'GESTOR'],
    icon: 'book',
    group: 'intelligence',
    order: 9,
    match: 'startsWith',
    notes: 'Biblioteca técnica — Fase 2'
  },
  {
    id: 'nav-methodologies',
    label: 'Metodologias',
    route: '/library/methodologies',
    status: 'planned',
    sports: ['multi'],
    roles: ['TREINADOR', 'PREPARADOR_FISICO', 'GESTOR'],
    icon: 'book-open',
    group: 'intelligence',
    order: 10,
    notes: 'Metodologias de treinamento (ex-coaching_core)'
  },
  {
    id: 'nav-research',
    label: 'Pesquisa',
    route: null,
    status: 'planned',
    sports: ['multi'],
    roles: [],
    icon: 'search',
    group: 'intelligence',
    order: 10,
    capability: 'arena.researcher.read',
    notes: 'Ferramenta de pesquisa — stub page, hidden until GovernanceResearchPage is wired'
  },

  // Scouting Dashboard + Shortlist — removidos (RECRUITER → Fase 2)

  // management section migrada para grupo 'gestor' (v3.0)

  // ──────────────────────────────────────────────────────────────────────────
  // 11. GOVERNANCE — HIDDEN (moved to ops.vukz.ai exclusively)
  // ──────────────────────────────────────────────────────────────────────────

  // ──────────────────────────────────────────────────────────────────────────
  // NUTRICIONISTA — Workspace
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'nav-nutri-day',
    label: 'Hoje',
    route: '/staff/today',
    status: 'active',
    sports: ['multi'],
    roles: ['NUTRICIONISTA'],
    icon: 'calendar-check',
    group: 'nutricionista',
    order: -1,
    match: 'exact',
    notes: 'Roster-do-dia nutricional: refeições marcadas vs planejadas por atleta (home pós-login)'
  },
  {
    id: 'nav-nutri-dashboard',
    label: 'Dashboard',
    route: '/nutrition/dashboard',
    status: 'active',
    sports: ['swimming', 'multi'],
    roles: ['NUTRICIONISTA'],
    icon: 'layout-dashboard',
    group: 'nutricionista',
    order: 1,
    notes: 'Painel consolidado do nutricionista'
  },
  {
    id: 'nav-nutri-athletes',
    label: 'Atletas',
    route: '/nutrition/athletes',
    status: 'active',
    sports: ['swimming', 'multi'],
    roles: ['NUTRICIONISTA'],
    icon: 'users',
    group: 'nutricionista',
    order: 2,
    notes: 'Lista de atletas vinculados'
  },
  {
    id: 'nav-nutri-create',
    label: 'Criar dieta',
    route: '/nutrition/novo',
    status: 'active',
    sports: ['swimming', 'multi'],
    roles: ['NUTRICIONISTA'],
    icon: 'plus-circle',
    group: 'nutricionista',
    order: 2.5,
    notes: 'Atalho direto ao wizard de dieta (rota já existia, guardada) — item C do ciclo A-nutri 07/08/2026'
  },
  {
    id: 'nav-nutri-plans',
    label: 'Dietas dos atletas',
    route: '/nutrition/diets',
    status: 'active',
    sports: ['swimming', 'multi'],
    roles: ['NUTRICIONISTA'],
    icon: 'file-text',
    group: 'nutricionista',
    order: 3,
    notes: 'N4: aponta p/ DietsPage (N2-UI). Antes apontava p/ EntryPage (formulário morto: gravava athlete_name sem id — backend exige athlete_id, todo submit era 400). Rota antiga /nutrition/plans virou redirect.'
  },
  {
    id: 'nav-nutri-alerts',
    label: 'Alertas',
    route: '/nutrition/alerts',
    status: 'active',
    sports: ['swimming', 'multi'],
    roles: ['NUTRICIONISTA'],
    icon: 'bell-ring',
    group: 'nutricionista',
    order: 4,
    notes: 'Alertas nutricionais ativos'
  },
  // N4: 'nav-nutri-communication' REMOVIDO — era navegação fantasma:
  // /nutrition/notes renderizava NutritionDashboardPage (mesma tela que
  // nav-nutri-dashboard, label diferente). Tela de notas não existe.
  // Quando existir, recriar o item apontando pra ela.

  // ──────────────────────────────────────────────────────────────────────────
  // BIOMECÂNICO — Workspace
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'nav-biomech-dashboard',
    label: 'Dashboard',
    route: '/biomechanics/dashboard',
    status: 'active',
    sports: ['swimming', 'multi'],
    roles: ['BIOMECANICO'],
    icon: 'layout-dashboard',
    group: 'biomecanico',
    order: 1,
    notes: 'Painel consolidado do biomecânico'
  },
  {
    id: 'nav-biomech-sessions',
    label: 'Sessões de Video',
    route: '/biomechanics/sessions',
    status: 'active',
    sports: ['swimming', 'multi'],
    roles: ['BIOMECANICO'],
    icon: 'video',
    group: 'biomecanico',
    order: 2,
    notes: 'Lista de sessões de análise biomecânica'
  },
  {
    id: 'nav-biomech-workspace',
    label: 'Ficha Biomecânica',
    route: '/biomechanics/workspace',
    status: 'active',
    sports: ['swimming', 'multi'],
    roles: ['BIOMECANICO'],
    icon: 'scan-line',
    group: 'biomecanico',
    order: 3,
    match: 'startsWith',
    notes: 'Workspace completo: ficha por atleta, métricas, histórico, enviar para coach/atleta'
  },
  {
    id: 'nav-biomech-athletes',
    label: 'Atletas',
    route: '/biomechanics/athletes',
    status: 'active',
    sports: ['swimming', 'multi'],
    roles: ['BIOMECANICO'],
    icon: 'users',
    group: 'biomecanico',
    order: 4,
    notes: 'Atletas vinculados com evolução técnica'
  },
  {
    id: 'nav-biomech-reports',
    label: 'Relatórios',
    route: '/biomechanics/reports',
    status: 'active',
    sports: ['swimming', 'multi'],
    roles: ['BIOMECANICO'],
    icon: 'file-bar-chart',
    group: 'biomecanico',
    order: 4,
    notes: 'Laudos técnicos e análises'
  },
  {
    id: 'nav-biomech-comparisons',
    label: 'Comparações',
    route: '/biomechanics/comparisons',
    status: 'active',
    sports: ['swimming', 'multi'],
    roles: ['BIOMECANICO'],
    icon: 'columns',
    group: 'biomecanico',
    order: 5,
    notes: 'Comparações lado-a-lado de sessões'
  },
  {
    id: 'nav-biomech-swim-vision',
    label: 'Swim Vision',
    route: '/swim-vision',
    status: 'active',
    sports: ['swimming'],
    roles: ['BIOMECANICO'],
    icon: 'goggles',
    group: 'biomecanico',
    order: 6,
    notes: 'Acesso direto ao módulo Swim Vision (Computer Vision)'
  },
  {
    id: 'nav-biomech-swim-vision-dashboard',
    label: 'Vision Dashboard',
    route: '/swim-vision/dashboard',
    status: 'active',
    sports: ['swimming'],
    roles: ['BIOMECANICO'],
    icon: 'dashboard',
    group: 'biomecanico',
    order: 7,
    notes: 'Dashboard de sessões e KPIs SwimVision para o biomecânico'
  },

  // ──────────────────────────────────────────────────────────────────────────
  // 12. INSTITUTIONAL (Links institucionais — sempre no final)
  // ──────────────────────────────────────────────────────────────────────────
  {
    id: 'nav-about',
    label: 'Sobre',
    route: '/sobre',
    status: 'active',
    sports: ['multi'],
    roles: [],
    icon: 'info',
    group: 'institutional',
    order: 1,
    match: 'exact',
    notes: 'Sobre a plataforma'
  },
  {
    id: 'nav-privacy',
    label: 'Privacidade',
    route: '/privacidade',
    status: 'active',
    sports: ['multi'],
    roles: [],
    icon: 'shield-check',
    group: 'institutional',
    order: 2,
    match: 'exact',
    notes: 'Política de privacidade'
  },
  {
    id: 'nav-terms',
    label: 'Termos',
    route: '/termos',
    status: 'active',
    sports: ['multi'],
    roles: [],
    icon: 'file-text',
    group: 'institutional',
    order: 3,
    match: 'exact',
    notes: 'Termos de uso'
  },
  {
    id: 'nav-contact',
    label: 'Contato',
    route: '/contato',
    status: 'active',
    sports: ['multi'],
    roles: [],
    icon: 'mail',
    group: 'institutional',
    order: 4,
    match: 'exact',
    notes: 'Fale conosco'
  }
];

// ════════════════════════════════════════════════════════════════════════════
// GROUP TITLES
// ════════════════════════════════════════════════════════════════════════════

export const GROUP_TITLES = {
  atleta: 'Minha Área',
  treinador: 'Minha Área',
  prep_fisico: 'Preparação Física',
  nutricionista: 'Nutrição Esportiva',
  biomecanico: 'Biomecânica',
  gestor: 'Performance',
  gestor_admin: 'Administração do Clube',
  agenda: 'Agenda',
  intelligence: 'Inteligência',
  governance: 'Administração',
  institutional: 'Links Institucionais'
};

export const GROUP_TITLES_EN = {
  atleta: 'My Area',
  treinador: 'My Area',
  prep_fisico: 'Physical Training',
  nutricionista: 'Sports Nutrition',
  biomecanico: 'Biomechanics',
  gestor: 'Performance',
  gestor_admin: 'Club Administration',
  agenda: 'Schedule',
  intelligence: 'Intelligence',
  governance: 'Administration',
  institutional: 'Institutional Links'
};

// English label overrides — falls back to PT label if not defined
const LABEL_EN = {
  'nav-athlete-day': 'Today',
  'nav-coach-day': 'Today',
  'nav-prep-day': 'Today',
  'nav-manager-day': 'Today',
  'nav-nutri-day': 'Today',
  'nav-athlete-home': 'VUKZ Assist',
  'nav-athlete-today': "Today's Training",
  'nav-athlete-treino-fisico': 'Physical Training',
  'nav-athlete-my-performance': 'My Performance',
  'nav-athlete-wellness': 'My Wellness',
  'nav-athlete-injuries': 'My Injuries',
  'nav-athlete-rehab': 'My Rehabilitation',
  'nav-athlete-nutrition': 'My Nutrition',
  'nav-athlete-profile': 'My Profile',
  'nav-athlete-messages': 'Messages',
  'nav-coach-home': 'VUKZ Assist',
  'nav-coach-dashboard': 'My Athletes',
  'nav-coach-groups': 'Training Groups',
  'nav-coach-training': 'Training',
  'nav-coach-treino-fisico': 'Physical Training',
  'nav-coach-my-performance': 'Athlete Performance',
  'nav-coach-periodization': 'Periodization',
  'nav-coach-session-builder': 'Session Builder',
  'nav-coach-health-overview': 'Squad Wellness',
  'nav-coach-nutrition': 'Squad Nutrition',
  'nav-coach-injuries': 'Injury Control',
  'nav-coach-clearance': 'Medical Clearances',
  'nav-coach-messages': 'Messages',
  'nav-prep-home': 'VUKZ Assist',
  'nav-prep-team': 'My Squad',
  'nav-prep-plans': 'Create Training',
  'nav-prep-wellness-squad': 'Squad Wellness',
  'nav-prep-sessions': 'Sessions',
  'nav-prep-athletes': 'Athletes',
  'nav-prep-dashboard-fisico': 'Physical Dashboard',
  'nav-prep-load': 'Load Monitoring',
  'nav-prep-rehab': 'Rehabilitation',
  'nav-prep-screening': 'Physical Assessments',
  'nav-prep-nutrition': 'Nutrition',
  'nav-prep-periodization': 'Periodization',
  'nav-prep-automation': 'Automations',
  'nav-prep-messages': 'Messages',
  'nav-manager-home': 'VUKZ Assist',
  'nav-manager-dashboard': 'Performance Overview',
  'nav-manager-categories': 'Categories',
  'nav-manager-methodology': 'Methodology',
  'nav-manager-interventions': 'Interventions',
  'nav-manager-strategic-plan': 'Strategic Plan',
  'nav-manager-staff-permissions': 'Staff Scope',
  'nav-manager-setup': 'Club Setup',
  'nav-manager-claim': 'Official Data',
  'nav-manager-roster': 'Roster & Staff',
  'nav-manager-messages': 'Messages',
  'nav-manager-financial': 'Financial',
  'nav-manager-support': 'Support',
  'nav-agenda-atleta': 'Schedule',
  'nav-agenda-treinador': 'Schedule',
  'nav-agenda-prep': 'Schedule',
  'nav-agenda-gestor': 'Schedule',
  'nav-analytics': 'Analytics',
  'nav-stats-swimming': 'Swimming Rankings',
  'nav-competition-results': 'Competitions',
  'nav-swim-vision': 'Swim Vision',
  'nav-swim-vision-dashboard': 'Vision Dashboard',
  'nav-track-vision': 'TrackVision',
  'nav-track-vision-dashboard': 'TrackVision Sessions',
  'nav-tactics-designer': 'Tactics Designer',
  'nav-playbook-designer': 'Playbook Designer',
  'nav-library': 'Arena Library',
  'nav-methodologies': 'Methodologies',
  'nav-research': 'Research',
  'nav-nutri-dashboard': 'Dashboard',
  'nav-nutri-athletes': 'Athletes',
  'nav-nutri-plans': 'Athlete diets',
  'nav-nutri-alerts': 'Alerts',
  'nav-biomech-dashboard': 'Dashboard',
  'nav-biomech-sessions': 'Video Sessions',
  'nav-biomech-athletes': 'Athletes',
  'nav-biomech-reports': 'Reports',
  'nav-biomech-comparisons': 'Comparisons',
  'nav-about': 'About',
  'nav-privacy': 'Privacy',
  'nav-terms': 'Terms of Use',
  'nav-contact': 'Contact'
};

/**
 * Returns the nav item label for the given language.
 * Falls back to Portuguese if no English translation is defined.
 * @param {NavItem} item
 * @param {'pt'|'en'} lang
 * @returns {string}
 */
export const getLabelForLang = (item, lang) =>
  lang === 'en' ? (LABEL_EN[item.id] || item.label) : item.label;

// ════════════════════════════════════════════════════════════════════════════
// GROUP ICONS (PROMPT 7)
// ════════════════════════════════════════════════════════════════════════════

export const GROUP_ICONS = {
  atleta: 'home',
  treinador: 'clipboard',
  prep_fisico: 'dumbbell',
  nutricionista: 'apple',
  biomecanico: 'scan-line',
  gestor: 'bar-chart-3',
  gestor_admin: 'settings',
  agenda: 'calendar',
  intelligence: 'brain',
  governance: 'shield',
  institutional: 'info'
};

// ════════════════════════════════════════════════════════════════════════════
// COLLAPSIBLE GROUPS
// ════════════════════════════════════════════════════════════════════════════

export const COLLAPSIBLE_GROUPS = [
  'atleta',        // Role group colapsável
  'treinador',     // Role group colapsável
  'prep_fisico',   // Role group colapsável
  'nutricionista', // Role group colapsável
  'biomecanico',   // Role group colapsável
  'gestor',        // Role group colapsável
  'gestor_admin',  // Admin group colapsável
  'intelligence',  // Inteligência colapsável
  'governance'     // Administração colapsável
];

// ════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Resolve rota dinâmica (suporte para :sportId e functions)
 * @param {string|Function|null} route - Rota (string, function ou null)
 * @param {Object} context - Contexto de resolução
 * @param {string} context.activeSport - Esporte ativo
 * @returns {string|null} - Rota resolvida ou null (se planned)
 */
export const resolveRoute = (route, context = {}) => {
  // Se route é null → item planejado (disabled)
  if (route === null) return null;
  
  // Se route é function → executar com context
  if (typeof route === 'function') {
    return route(context);
  }
  
  // Se route é string → substituir :sportId
  if (typeof route === 'string') {
    const activeSport = context.activeSport || 'swimming';
    return route.replace(':sportId', activeSport);
  }
  
  // Fallback: retornar como está
  return route;
};

/**
 * Retorna rota do dashboard baseado no role
 * @param {Role} roleKey 
 * @returns {string}
 */
export const getDashboardRouteForRole = (roleKey) => {
  const normalized = String(roleKey || '').toUpperCase();
  switch (normalized) {
    case 'OPERATIONS_ADMIN':
      return '/governance';
    case 'ADMIN':
      return '/governance';
    // ── 4 perfis do lançamento ────
    // 07/07/2026 (Today multi-papel): staff loga no roster-do-dia — o atleta
    // completa, o responsável VÊ preencher. Assist segue no sidebar.
    case 'TREINADOR':
    case 'GESTOR':
    case 'PREPARADOR_FISICO':
    case 'NUTRICIONISTA':
      return '/staff/today';
    case 'BIOMECANICO':
      return '/biomechanics/dashboard';
    // ATLETA entra na página-do-dia (07/07/2026): treino de hoje + dieta
    // do dia. VUKZ Assist desceu pro fim do grupo na sidebar.
    case 'ATLETA':
      return '/athlete/today';
    default:
      return '/assist';
  }
};

/**
 * Verifica se um item está habilitado (active + feature flags)
 * @param {NavItem} item 
 * @param {Object} flags - Feature flags object (default: FEATURES)
 * @returns {boolean}
 */
export const isItemEnabled = (item, flags = FEATURES, isSuperAdmin = false, hasCapability = () => false) => {
  // Planejados sempre disabled — EXCETO:
  //  a) módulos internos liberados para super admin (superAdminActive: true);
  //  b) capability concedida pelo backend (GET /api/me) — HOTFIX 14/07/2026
  //     (Minas): SwimVision via BIOMECHANICS_ALLOWED_EMAILS. Fail-closed:
  //     hasCapability default retorna false até as capabilities carregarem.
  if (item.status === 'planned') {
    if (isSuperAdmin && item.superAdminActive && item.route !== null) return true;
    return Boolean(item.capability && item.route !== null && hasCapability(item.capability));
  }
  
  // Se route é null → disabled (mesmo que status seja 'active')
  if (item.route === null) return false;
  
  // Se tem feature flag, verificar se está ativa
  if (item.featureFlag) {
    return Boolean(flags[item.featureFlag]);
  }
  
  // Active e sem feature flag = enabled
  return item.status === 'active';
};

/**
 * Filtra itens por role, sport, e feature flags
 * 
 * LÓGICA DE VISIBILIDADE (ordem AND):
 * visible = (roleMatch || roles.length === 0) && 
 *           (featureFlagOK) && 
 *           (capability ? hasCapability(cap) : true)
 * 
 * @param {Object} params
 * @param {Role} params.role - Role ativo do usuário
 * @param {SportKey} params.sport - Esporte ativo
 * @param {Object} params.flags - Feature flags (default: FEATURES)
 * @param {boolean} params.isAdmin - Se é admin
 * @param {boolean} params.superMode - Se super mode está ativo (para ADMIN)
 * @param {Function} params.hasCapability - Função para verificar capabilities
 * @returns {NavItem[]}
 */
/**
 * P3.2 — Capability de PLANO exigida por item de navegação (capabilities.v1).
 * O plano determina "o clube possui este módulo?". Itens fora deste mapa
 * não dependem de plano. Enforcement duro é do backend
 * (require_capability → 403 CAPABILITY_NOT_IN_PLAN).
 */
export const PLAN_CAPABILITY_BY_ITEM = {
  'nav-athlete-nutrition': 'nutrition',
  'nav-coach-nutrition': 'nutrition',
  'nav-prep-nutrition': 'nutrition',
  'nav-manager-athlete-diets': 'nutrition',
  'nav-nutri-day': 'nutrition',
  'nav-nutri-dashboard': 'nutrition',
  'nav-nutri-athletes': 'nutrition',
  'nav-nutri-create': 'nutrition',
  'nav-nutri-plans': 'nutrition',
  'nav-nutri-alerts': 'nutrition',
  'nav-coach-treino-fisico': 'physical',
  'nav-prep-dashboard-fisico': 'physical',
};

/**
 * P3.2 — Capability de PLANO exigida por GRUPO inteiro. Cobre módulos
 * profissionais completos sem listar item a item.
 */
export const PLAN_CAPABILITY_BY_GROUP = {
  prep_fisico: 'physical',
  nutricionista: 'nutrition',
};

export const getSidebarItems = ({
  role,
  userRoles = [],          // ← NEW: token roles (source of truth)
  sport = 'multi',
  flags = FEATURES,
  isAdmin = false,
  superMode = false,
  hasCapability = () => false,
  // P3.2 — capabilities do PLANO (via usePlanCapabilities/GET /me/context).
  // O hook é FAIL-CLOSED (sem contexto → módulos opcionais ocultos).
  // Default true apenas para chamadores legados que não passam o hook.
  hasPlanCapability = () => true
} = {}) => {
  const normalizedRole = String(role || '').toUpperCase();
  const normalizedSport = String(sport || 'multi').toLowerCase();

  const filtered = SIDEBAR_ITEMS.filter((item) => {
    // ─────────────────────────────────────────────────────────────────────
    // 0. ESCOPO DA DEMO — itens fora do escopo inicial (config/demoScope.js)
    //    NÃO substitui RBAC; só navegação. Reativação: REACT_APP_FULL_SCOPE=true
    // ─────────────────────────────────────────────────────────────────────
    if (isNavItemOutOfScope(item.id)) return false;

    // ─────────────────────────────────────────────────────────────────────
    // 1. FILTRO DE ESPORTE
    // ─────────────────────────────────────────────────────────────────────
    const sportMatch = item.sports.includes('multi') || item.sports.includes(normalizedSport);
    if (!sportMatch) return false;

    // ─────────────────────────────────────────────────────────────────────
    // 2. SUPER MODE (ADMIN vê tudo)
    // ─────────────────────────────────────────────────────────────────────
    if (isAdmin && superMode) return true;

    // ─────────────────────────────────────────────────────────────────────
    // 3. FILTRO DE ROLE (UX narrowing via activeRole)
    // ─────────────────────────────────────────────────────────────────────
    // SEMÂNTICA: roles: [] = público (TODOS os roles)
    const roleMatch = !item.roles || item.roles.length === 0 || 
                      (normalizedRole && item.roles.includes(normalizedRole));
    
    // ─────────────────────────────────────────────────────────────────────
    // 3b. TOKEN ROLE CHECK (P1 — sidebar never shows what token can't reach)
    // ─────────────────────────────────────────────────────────────────────
    // If item has restricted roles AND userRoles is provided,
    // verify the user's token actually has at least one matching role.
    // This prevents "click → Forbidden" friction.
    // ADMIN BYPASS: admins can switch to any role via RoleSwitcher
    let tokenRoleOK = true;
    if (!isAdmin && item.roles && item.roles.length > 0 && userRoles.length > 0) {
      tokenRoleOK = item.roles.some(r => userRoles.includes(r));
    }

    // ─────────────────────────────────────────────────────────────────────
    // 4. FEATURE FLAG
    // ─────────────────────────────────────────────────────────────────────
    const featureFlagOK = item.featureFlag ? Boolean(flags[item.featureFlag]) : true;
    
    // ─────────────────────────────────────────────────────────────────────
    // 5. CAPABILITY
    // ─────────────────────────────────────────────────────────────────────
    // Se tem capability, precisa hasCapability() OU isAdmin
    const capabilityOK = item.capability 
      ? (hasCapability(item.capability) || isAdmin) 
      : true;

    // ─────────────────────────────────────────────────────────────────────
    // 5b. CAPABILITY DE PLANO (P3.2 — fail-open, backend é autoridade)
    // ─────────────────────────────────────────────────────────────────────
    const planCap = PLAN_CAPABILITY_BY_ITEM[item.id] || PLAN_CAPABILITY_BY_GROUP[item.group];
    const planCapabilityOK = planCap ? hasPlanCapability(planCap) : true;

    // ─────────────────────────────────────────────────────────────────────
    // 6. LÓGICA AND
    // ─────────────────────────────────────────────────────────────────────
    return roleMatch && tokenRoleOK && featureFlagOK && capabilityOK && planCapabilityOK;
  });

  // ─────────────────────────────────────────────────────────────────────
  // DEDUPE BY PATH — prevents duplicate links from overlapping groups
  // ─────────────────────────────────────────────────────────────────────
  const seen = new Set();
  return filtered.filter(item => {
    const key = item.route || item.id;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Agrupa itens por group
 * @param {NavItem[]} items 
 * @returns {Object} - { group: [items] }
 */
export const groupItems = (items) => {
  if (!items || !Array.isArray(items)) return {};
  return items.reduce((acc, item) => {
    const group = item.group || 'other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});
};

/**
 * Ordena itens dentro de cada grupo por `order`
 * @param {Object} groupedItems - { group: [items] }
 * @returns {Object}
 */
export const sortGroupedItems = (groupedItems) => {
  Object.keys(groupedItems).forEach((group) => {
    groupedItems[group].sort((a, b) => (a.order || 0) - (b.order || 0));
  });
  return groupedItems;
};

// ════════════════════════════════════════════════════════════════════════════
// ICONS MAP (Lucide React)
// ════════════════════════════════════════════════════════════════════════════

export const ICONS = {
  home: 'Home',
  activity: 'Activity',
  users: 'Users',
  calendar: 'Calendar',
  dumbbell: 'Dumbbell',
  list: 'List',
  'bar-chart': 'BarChart',
  heart: 'Heart',
  clipboard: 'Clipboard',
  'calendar-range': 'CalendarRange',
  'plus-square': 'PlusSquare',
  'bar-chart-2': 'BarChart2',
  shield: 'Shield',
  award: 'Award',
  bandage: 'Bandage',
  'heart-pulse': 'HeartPulse',
  'clipboard-check': 'ClipboardCheck',
  'check-circle': 'CheckCircle',
  gauge: 'Gauge',
  video: 'Video',
  layout: 'Layout',
  'book-open': 'BookOpen',
  'trending-up': 'TrendingUp',
  brain: 'Brain',
  book: 'Book',
  search: 'Search',
  compass: 'Compass',
  star: 'Star',
  'layout-dashboard': 'LayoutDashboard',
  'check-square': 'CheckSquare',
  briefcase: 'Briefcase',
  'dollar-sign': 'DollarSign',
  'file-text': 'FileText',
  wrench: 'Wrench',
  'layout-grid': 'LayoutGrid',
  bot: 'Bot',
  radar: 'Radar',
  'trending-down': 'TrendingDown',
  'file-search': 'FileSearch',
  'alert-triangle': 'AlertTriangle',
  info: 'Info',
  'shield-check': 'ShieldCheck',
  mail: 'Mail',
  utensils: 'Utensils',
  zap: 'Zap'
};
